import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, ImageUp, Plus, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import Preloader from '@/components/Preloader';
import LogoFornecedor from '@/components/LogoFornecedor';
import { mascaraCnpj, mascaraTelefone } from './mascaras';
import type { Fornecedor } from './tipos';

type ContatoRascunho = { chave: string; id: number | null; nome: string; email: string; whatsapp: string; recebe_cotacao: boolean };

type Rascunho = {
  nome: string;
  apelido: string;
  cnpj: string;
  inscricao_estadual: string;
  telefone: string;
  site: string;
  observacoes: string;
  contatos: ContatoRascunho[];
};

const TAMANHO_MAXIMO = 1024 * 1024;
const TIPOS_DE_LOGO = ['image/png', 'image/jpeg', 'image/webp'];
const MENSAGEM_LOGO = 'A logo precisa ser PNG, JPG ou WEBP, até 1 MB.';

const CAMPO =
  'w-full rounded-md border bg-surface px-sm py-xs text-[13px] text-ink placeholder:text-muted focus:border-accent focus:ring-2 focus:ring-accent/20 focus:outline-none';

function paraRascunho(fornecedor: Fornecedor): Rascunho {
  return {
    nome: fornecedor.nome,
    apelido: fornecedor.apelido,
    cnpj: fornecedor.cnpj ?? '',
    inscricao_estadual: fornecedor.inscricao_estadual ?? '',
    telefone: fornecedor.telefone ?? '',
    site: fornecedor.site ?? '',
    observacoes: fornecedor.observacoes ?? '',
    contatos: fornecedor.contatos.map((contato) => ({
      chave: `contato-${contato.id}`,
      id: contato.id,
      nome: contato.nome,
      email: contato.email ?? '',
      whatsapp: contato.whatsapp ? mascaraTelefone(contato.whatsapp) : '',
      recebe_cotacao: contato.recebe_cotacao,
    })),
  };
}

function lerErros(falha: unknown): { campos: Record<string, string>; mensagem: string } {
  const dados = (falha as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }).response?.data;
  const campos = Object.fromEntries(Object.entries(dados?.errors ?? {}).map(([campo, mensagens]) => [campo, mensagens[0]]));

  return { campos, mensagem: dados?.errors ? 'Confira os campos marcados.' : (dados?.message ?? 'Não foi possível salvar.') };
}

function Campo({ rotulo, erro, dica, children }: { rotulo: string; erro?: string; dica?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-[2px] block text-[12px] font-bold text-ink-soft">{rotulo}</span>
      {children}
      {erro ? (
        <span className="mt-[2px] block text-[11px] font-bold text-danger">{erro}</span>
      ) : (
        dica && <span className="mt-[2px] block text-[11px] text-muted">{dica}</span>
      )}
    </label>
  );
}

/**
 * Edição de um fornecedor: logo (gravada na hora), dados da empresa e a lista
 * de contatos que recebem as cotações (gravados juntos no Salvar).
 */
function EdicaoFornecedor() {
  const { fornecedorId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const entradaDaLogo = useRef<HTMLInputElement>(null);

  const [rascunho, setRascunho] = useState<Rascunho | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<{ tom: 'ok' | 'erro'; texto: string } | null>(null);
  const [erroDaLogo, setErroDaLogo] = useState<string | null>(null);
  const [arrastando, setArrastando] = useState(false);

  const { data: fornecedor, isError } = useQuery({
    queryKey: ['fornecedor', fornecedorId],
    queryFn: async () => (await api.get<{ data: Fornecedor }>(`/fornecedores/${fornecedorId}`)).data.data,
  });

  // Enquanto nada foi mexido, o formulário mostra o que veio do servidor.
  const formulario = rascunho ?? (fornecedor ? paraRascunho(fornecedor) : null);
  const alterado = rascunho !== null && fornecedor !== undefined && JSON.stringify(rascunho) !== JSON.stringify(paraRascunho(fornecedor));

  useEffect(() => {
    if (!alterado) return;
    const avisar = (evento: BeforeUnloadEvent) => evento.preventDefault();
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [alterado]);

  // Erro de um campo não pode ficar preso depois que o comprador já corrigiu o
  // valor: cada digitação apaga o erro do próprio campo (ou, ao mexer na lista
  // de contatos, os erros por índice — que ficariam no índice errado assim que
  // a lista muda de tamanho).
  const limparErros = (chaves: string[]) => {
    if (chaves.length === 0) return;
    setErros((atual) => {
      const proximo = { ...atual };
      for (const chave of chaves) delete proximo[chave];
      return proximo;
    });
  };

  const limparErrosDosContatos = () => {
    setErros((atual) => Object.fromEntries(Object.entries(atual).filter(([chave]) => chave !== 'contatos' && !chave.startsWith('contatos.'))));
  };

  const alterar = (mudanca: Partial<Rascunho>, chavesDeErro: string[] = []) => {
    if (!formulario) return;
    setAviso(null);
    limparErros(chavesDeErro);
    setRascunho({ ...formulario, ...mudanca });
  };

  const alterarContato = (chave: string, mudanca: Partial<ContatoRascunho>) => {
    if (!formulario) return;
    const indice = formulario.contatos.findIndex((c) => c.chave === chave);
    const chavesDeErro = indice === -1 ? [] : Object.keys(mudanca).map((campo) => `contatos.${indice}.${campo}`);
    alterar({ contatos: formulario.contatos.map((c) => (c.chave === chave ? { ...c, ...mudanca } : c)) }, chavesDeErro);
  };

  // Adicionar ou remover contato muda os índices de todo mundo: os erros por
  // índice não fazem mais sentido e saem todos juntos.
  const alterarListaDeContatos = (contatos: ContatoRascunho[]) => {
    if (!formulario) return;
    setAviso(null);
    limparErrosDosContatos();
    setRascunho({ ...formulario, contatos });
  };

  const salvar = useMutation({
    mutationFn: async (dados: Rascunho) =>
      (
        await api.put<{ data: Fornecedor; message: string }>(`/fornecedores/${fornecedorId}`, {
          nome: dados.nome,
          apelido: dados.apelido,
          cnpj: dados.cnpj || null,
          inscricao_estadual: dados.inscricao_estadual || null,
          telefone: dados.telefone || null,
          site: dados.site || null,
          observacoes: dados.observacoes || null,
          contatos: dados.contatos.map((c) => ({
            id: c.id,
            nome: c.nome,
            email: c.email || null,
            whatsapp: c.whatsapp || null,
            recebe_cotacao: c.recebe_cotacao,
          })),
        })
      ).data,
    onMutate: () => {
      setErros({});
      setAviso(null);
    },
    onSuccess: (resposta) => {
      queryClient.setQueryData(['fornecedor', fornecedorId], resposta.data);
      setRascunho(null);
      setAviso({ tom: 'ok', texto: resposta.message });
      void queryClient.invalidateQueries({ queryKey: ['fornecedores'] });
    },
    onError: (falha) => {
      const { campos, mensagem } = lerErros(falha);
      setErros(campos);
      setAviso({ tom: 'erro', texto: mensagem });
    },
  });

  // A logo vale na hora: o cache do fornecedor recebe só a logo nova, sem
  // descartar o que o comprador já digitou no formulário.
  const trocarLogoNoCache = (logoUrl: string | null) => {
    queryClient.setQueryData<Fornecedor>(['fornecedor', fornecedorId], (anterior) => (anterior ? { ...anterior, logo_url: logoUrl } : anterior));
    void queryClient.invalidateQueries({ queryKey: ['fornecedores'] });
  };

  const enviarLogo = useMutation({
    mutationFn: async (arquivo: File) => {
      const corpo = new FormData();
      corpo.append('logo', arquivo);
      return (await api.post<{ data: Fornecedor }>(`/fornecedores/${fornecedorId}/logo`, corpo)).data.data;
    },
    onMutate: () => setErroDaLogo(null),
    onSuccess: (atualizado) => trocarLogoNoCache(atualizado.logo_url),
    onError: (falha) => setErroDaLogo(lerErros(falha).campos.logo ?? MENSAGEM_LOGO),
  });

  const removerLogo = useMutation({
    mutationFn: async () => (await api.delete<{ data: Fornecedor }>(`/fornecedores/${fornecedorId}/logo`)).data.data,
    onMutate: () => setErroDaLogo(null),
    onSuccess: (atualizado) => trocarLogoNoCache(atualizado.logo_url),
    onError: () => setErroDaLogo('Não foi possível remover a logo.'),
  });

  // Enquanto uma logo está sendo enviada ou removida, a outra ação fica presa:
  // evita a segunda requisição pisar no resultado da primeira.
  const logoOcupada = enviarLogo.isPending || removerLogo.isPending;

  const escolherLogo = (arquivo: File | undefined) => {
    if (!arquivo || logoOcupada) return;
    if (!TIPOS_DE_LOGO.includes(arquivo.type) || arquivo.size > TAMANHO_MAXIMO) {
      setErroDaLogo(MENSAGEM_LOGO);
      return;
    }
    enviarLogo.mutate(arquivo);
  };

  const cancelar = () => {
    if (alterado && !window.confirm('As alterações não salvas serão perdidas. Sair mesmo assim?')) return;
    navigate('/fornecedores');
  };

  if (isError) {
    return <p className="px-lg py-lg text-[14px] text-danger">Não foi possível carregar o fornecedor.</p>;
  }

  if (!fornecedor || !formulario) {
    return <Preloader telaCheia mensagem="Carregando fornecedor…" className="py-3xl" />;
  }

  const recebem = formulario.contatos.filter((c) => c.recebe_cotacao).length;
  const borda = (campo: string) => (erros[campo] ? 'border-danger' : 'border-border');

  return (
    <div className="px-lg pt-lg">
      <Link
        to="/fornecedores"
        onClick={(evento) => {
          evento.preventDefault();
          cancelar();
        }}
        className="mb-sm inline-flex items-center gap-xs text-[12px] font-bold text-accent hover:underline"
      >
        <ArrowLeft size={13} aria-hidden="true" />
        Fornecedores
      </Link>
      {/* A logo é pequena (algo como 200×200, fundo transparente): fica ao lado do
          nome, como a marca do fornecedor, e não num quadro grande à parte. */}
      <div className="flex items-center gap-md">
        <div
          onDragOver={(evento) => {
            evento.preventDefault();
            if (logoOcupada) return;
            setArrastando(true);
          }}
          onDragLeave={() => setArrastando(false)}
          onDrop={(evento) => {
            evento.preventDefault();
            setArrastando(false);
            if (logoOcupada) return;
            escolherLogo(evento.dataTransfer.files[0]);
          }}
          title="Arraste a logo aqui"
          className={`flex size-[72px] shrink-0 items-center justify-center rounded-md border border-dashed ${
            arrastando ? 'border-accent bg-accent/10' : fornecedor.logo_url ? 'border-transparent' : 'border-border'
          }`}
        >
          {enviarLogo.isPending ? (
            <Preloader tamanho="sm" />
          ) : fornecedor.logo_url ? (
            <LogoFornecedor logoUrl={fornecedor.logo_url} apelido={fornecedor.apelido} tamanho="quadro" className="bg-transparent p-0" />
          ) : (
            <ImageUp size={22} className="text-muted" aria-hidden="true" />
          )}
        </div>
        <div className="min-w-0">
          <h1 className="text-[18px] font-bold text-ink">{fornecedor.apelido}</h1>
          <p className="text-[13px] text-muted">{fornecedor.nome}</p>
          <input
            ref={entradaDaLogo}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="sr-only"
            onChange={(evento) => {
              escolherLogo(evento.target.files?.[0]);
              evento.target.value = '';
            }}
          />
          <div className="mt-xs flex flex-wrap items-center gap-x-sm gap-y-[2px] text-[12px]">
            <button
              type="button"
              onClick={() => entradaDaLogo.current?.click()}
              disabled={logoOcupada}
              className="inline-flex items-center gap-xs font-bold text-accent hover:underline disabled:opacity-40"
            >
              <ImageUp size={13} aria-hidden="true" />
              {fornecedor.logo_url ? 'Trocar logo' : 'Enviar logo'}
            </button>
            {fornecedor.logo_url && (
              <button
                type="button"
                onClick={() => removerLogo.mutate()}
                disabled={logoOcupada}
                className="font-bold text-ink-soft hover:text-danger disabled:opacity-40"
              >
                {removerLogo.isPending ? 'Removendo…' : 'Remover'}
              </button>
            )}
            <span className={erroDaLogo ? 'font-bold text-danger' : 'text-muted'}>
              {erroDaLogo ?? 'PNG, JPG ou WEBP, até 1 MB — de preferência quadrada, com fundo transparente.'}
            </span>
          </div>
        </div>
      </div>

      {aviso && (
        <p
          className={`mt-md flex items-center gap-xs rounded-md border px-md py-sm text-[13px] font-bold ${
            aviso.tom === 'ok' ? 'border-success/30 bg-success/5 text-success' : 'border-danger/30 bg-danger/5 text-danger'
          }`}
        >
          {aviso.tom === 'ok' && <CheckCircle2 size={14} aria-hidden="true" />}
          {aviso.texto}
        </p>
      )}

      <div className="mt-lg">
          {/* Enquanto o Salvar está em voo, ninguém mexe no formulário — evita
              perder digitação que chegaria depois da resposta do servidor.
              A logo fica fora: ela grava na hora, sem depender do Salvar. */}
          <fieldset disabled={salvar.isPending} className="contents space-y-lg">
          <section className="rounded-lg border border-border bg-surface p-md">
            <h2 className="mb-sm text-[14px] font-bold text-ink">Dados da empresa</h2>
            <div className="grid gap-md sm:grid-cols-2">
              <Campo rotulo="Razão social" erro={erros.nome}>
                <input value={formulario.nome} maxLength={255} onChange={(e) => alterar({ nome: e.target.value }, ['nome'])} className={`${CAMPO} ${borda('nome')}`} />
              </Campo>
              <Campo rotulo="Nome curto" erro={erros.apelido} dica="Usado para reconhecer as propostas do fornecedor; não pode ser alterado.">
                <input value={formulario.apelido} readOnly maxLength={40} className={`${CAMPO} ${borda('apelido')} bg-surface-subtle text-muted`} />
              </Campo>
              <Campo rotulo="CNPJ" erro={erros.cnpj}>
                <input value={formulario.cnpj} inputMode="numeric" onChange={(e) => alterar({ cnpj: mascaraCnpj(e.target.value) }, ['cnpj'])} placeholder="00.000.000/0000-00" className={`${CAMPO} ${borda('cnpj')}`} />
              </Campo>
              <Campo rotulo="Inscrição estadual" erro={erros.inscricao_estadual}>
                <input value={formulario.inscricao_estadual} maxLength={20} onChange={(e) => alterar({ inscricao_estadual: e.target.value }, ['inscricao_estadual'])} className={`${CAMPO} ${borda('inscricao_estadual')}`} />
              </Campo>
              <Campo rotulo="Telefone" erro={erros.telefone}>
                <input value={formulario.telefone} inputMode="tel" onChange={(e) => alterar({ telefone: mascaraTelefone(e.target.value) }, ['telefone'])} placeholder="(51) 3333-4444" className={`${CAMPO} ${borda('telefone')}`} />
              </Campo>
              <Campo rotulo="Site" erro={erros.site}>
                <input value={formulario.site} maxLength={255} onChange={(e) => alterar({ site: e.target.value }, ['site'])} placeholder="www.fornecedor.com.br" className={`${CAMPO} ${borda('site')}`} />
              </Campo>
            </div>
            <div className="mt-md">
              <Campo rotulo="Observações" erro={erros.observacoes}>
                <textarea value={formulario.observacoes} rows={3} maxLength={5000} onChange={(e) => alterar({ observacoes: e.target.value }, ['observacoes'])} className={`${CAMPO} ${borda('observacoes')}`} />
              </Campo>
            </div>
          </section>

          <section className="rounded-lg border border-border bg-surface p-md">
            <div className="mb-sm flex flex-wrap items-baseline justify-between gap-sm">
              <h2 className="text-[14px] font-bold text-ink">Contatos</h2>
              <span className={`text-[12px] font-bold ${recebem ? 'text-muted' : 'text-warning'}`}>
                {recebem === 0 ? 'Nenhum contato recebe cotação' : recebem === 1 ? '1 recebe cotação' : `${recebem} recebem cotação`}
              </span>
            </div>
            {erros.contatos && <p className="mb-sm text-[12px] font-bold text-danger">{erros.contatos}</p>}

            {formulario.contatos.length === 0 ? (
              <p className="rounded-md border border-dashed border-border px-md py-md text-center text-[13px] text-muted">Nenhum contato cadastrado</p>
            ) : (
              <ul className="space-y-sm">
                {formulario.contatos.map((contato, indice) => (
                  <li key={contato.chave} className="grid items-start gap-sm rounded-md border border-border p-sm md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto_auto]">
                    <Campo rotulo="Nome" erro={erros[`contatos.${indice}.nome`]}>
                      <input value={contato.nome} maxLength={120} onChange={(e) => alterarContato(contato.chave, { nome: e.target.value })} placeholder="Ana — vendas" className={`${CAMPO} ${borda(`contatos.${indice}.nome`)}`} />
                    </Campo>
                    <Campo rotulo="E-mail" erro={erros[`contatos.${indice}.email`]}>
                      <input value={contato.email} type="email" maxLength={255} onChange={(e) => alterarContato(contato.chave, { email: e.target.value })} className={`${CAMPO} ${borda(`contatos.${indice}.email`)}`} />
                    </Campo>
                    <Campo rotulo="WhatsApp" erro={erros[`contatos.${indice}.whatsapp`]}>
                      <input value={contato.whatsapp} inputMode="tel" onChange={(e) => alterarContato(contato.chave, { whatsapp: mascaraTelefone(e.target.value) })} placeholder="(51) 99999-8888" className={`${CAMPO} ${borda(`contatos.${indice}.whatsapp`)}`} />
                    </Campo>
                    <label className="flex items-center gap-xs pt-[22px] text-[12px] font-bold text-ink-soft">
                      <input type="checkbox" checked={contato.recebe_cotacao} onChange={(e) => alterarContato(contato.chave, { recebe_cotacao: e.target.checked })} />
                      Recebe cotação
                    </label>
                    <button
                      type="button"
                      onClick={() => alterarListaDeContatos(formulario.contatos.filter((c) => c.chave !== contato.chave))}
                      aria-label={`Remover o contato ${contato.nome || indice + 1}`}
                      className="mt-[20px] rounded-md p-xs text-muted hover:bg-danger/10 hover:text-danger"
                    >
                      <Trash2 size={15} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <button
              type="button"
              onClick={() =>
                alterarListaDeContatos([
                  ...formulario.contatos,
                  { chave: `novo-${Date.now()}-${formulario.contatos.length}`, id: null, nome: '', email: '', whatsapp: '', recebe_cotacao: true },
                ])
              }
              className="mt-sm inline-flex items-center gap-xs text-[13px] font-bold text-accent hover:underline"
            >
              <Plus size={14} aria-hidden="true" />
              Adicionar contato
            </button>
          </section>
          </fieldset>
      </div>

      {/* Grudado no pé da tela enquanto o formulário rola, dentro da área do conteúdo. */}
      <div className="sticky bottom-0 z-30 -mx-lg mt-lg border-t border-border bg-surface px-lg py-sm">
        <div className="flex items-center justify-end gap-sm">
          {alterado && <span className="mr-auto text-[12px] text-muted">Alterações não salvas</span>}
          <button type="button" onClick={cancelar} className="rounded-md border border-border px-md py-sm text-[13px] font-bold text-ink hover:bg-surface-subtle">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => salvar.mutate(formulario)}
            disabled={!alterado || salvar.isPending}
            className="rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Trocar de fornecedor pela URL remonta a tela: o rascunho de um não vaza para o outro. */
export default function FornecedorPage() {
  const { fornecedorId } = useParams();

  return <EdicaoFornecedor key={fornecedorId} />;
}
